import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import Swal from 'sweetalert2';
import {
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight,
  MdDashboard,
  MdPeople,
  MdShoppingCart,
  MdFactory,
  MdContacts,
  MdInventory,
  MdBarChart,
  MdSettings,
  MdLogout,
  MdEdit,
  MdAccountCircle,
  MdEmail,
  MdPhone,
  MdPerson,
  MdVerifiedUser,
  MdChevronRight,
  MdExpandMore,
  MdLock,
  MdBusiness,
  MdCategory,
  MdLocalShipping,
  MdWarehouse,
  MdAssignment,
  MdReceipt,
  MdAssessment,
  MdTrendingUp,
  MdShoppingBag,
  MdBuild,
  MdQrCodeScanner,
  MdMenu,
  MdClose,
  MdHelp,
  MdHome,
  MdStore,
  MdGroup,
  MdPersonAdd,
  MdCategory as MdCategoryIcon,
  MdBusinessCenter,
  MdLocalMall,
  MdAttachMoney,
  MdProductionQuantityLimits,
  MdInventory2,
  MdReceiptLong,
  MdLocalShipping as MdLocalShippingIcon,
  MdFactCheck,
  MdPrecisionManufacturing,
  MdSummarize,
  MdPrint,
  MdFileCopy,
  MdFullscreen,
  MdFullscreenExit
} from 'react-icons/md';
import {
  FaDatabase,
  FaUserShield,
  FaBell,
  FaCogs,
  FaClipboardList,
  FaExchangeAlt,
  FaTruckLoading,
  FaBoxOpen,
  FaChartLine
} from 'react-icons/fa';
import { FiSearch } from 'react-icons/fi';
import { RiDashboardFill } from 'react-icons/ri';
import companyLogo from './synergy-logo-white.png';
import './SidebarPremium.css';

/* ── Moon icon (SVG) ── */
const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

/* ── Sun icon (SVG) ── */
const SunIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

const Sidebar = ({ children }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [activeLink, setActiveLink] = useState('');
  const [openCard, setOpenCard] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [hoveredIcon, setHoveredIcon] = useState(null);
  const [flyoutPos, setFlyoutPos] = useState({ top: 0, left: 0 });
  const [expandedSections, setExpandedSections] = useState({
    masters: false,
    userMasters: false,
    crm: false,
    sales: false,
    purchase: false,
    production: false,
    inventory: false
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDayMode, setIsDayMode] = useState(true);
  const [currentPageTitle, setCurrentPageTitle] = useState('Dashboard');
  const [isFullScreen, setIsFullScreen] = useState(false);

  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef(null);
  const profileRef = useRef(null);
  const sidebarRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const currentOpenIconRef = useRef(null);

  const navData = {
    dashboard: {
      title: 'Dashboard',
      icon: <RiDashboardFill />,
      iconColor: '#2dd4bf',
      activeIconColor: '#ffffff',
      path: '/home',
      bgGradient: 'linear-gradient(135deg, #2dd4bf, #14b8a6)'
    },
    masters: {
      title: 'Masters',
      icon: <FaDatabase />,
      iconColor: '#14b8a6',
      activeIconColor: '#ffffff',
      bgGradient: 'linear-gradient(135deg, #14b8a6, #0d9488)',
      items: [
        { to: '/productindex/1', label: 'Product', icon: <MdCategoryIcon />, iconColor: '#14b8a6' },
        { to: '/groupindex/1', label: 'Group', icon: <MdCategoryIcon />, iconColor: '#2dd4bf' },
        { to: '/customerindex/1', label: 'Customer', icon: <MdPeople />, iconColor: '#0d9488' },
        { to: '/supplierindex/1', label: 'Supplier', icon: <MdBusiness />, iconColor: '#0f766e' },
        { to: '/branchindex/1', label: 'Branch', icon: <MdBusiness />, iconColor: '#115e59' },
        { to: '/warehouseindex/1', label: 'Warehouse', icon: <MdWarehouse />, iconColor: '#134e4a' },
        { to: '/company', label: 'Company', icon: <MdBusiness />, iconColor: '#14b8a6' },
        { to: '/brandindex/1', label: 'Brand', icon: <MdCategoryIcon />, iconColor: '#2dd4bf' },
        { to: '/imagetabledindex/1', label: 'Drawing', icon: <MdQrCodeScanner />, iconColor: '#0d9488' }
      ]
    },
    userMasters: {
      title: 'User Masters',
      icon: <MdPeople />,
      iconColor: '#5eead4',
      activeIconColor: '#ffffff',
      bgGradient: 'linear-gradient(135deg, #5eead4, #2dd4bf)',
      items: [
        { to: '/staffindex/1', label: 'Staff', icon: <MdPeople />, iconColor: '#5eead4' },
        { to: '/department-list/1', label: 'Department', icon: <MdBusiness />, iconColor: '#2dd4bf' }
      ]
    },
    crm: {
      title: 'CRM',
      icon: <MdContacts />,
      iconColor: '#0d9488',
      activeIconColor: '#ffffff',
      bgGradient: 'linear-gradient(135deg, #0d9488, #0f766e)',
      items: [
        { to: '/salesEnquiryIndex/1', label: 'Sales Enquiry', icon: <MdAssignment />, iconColor: '#0d9488' },
        { to: '/quotationIndexTable/1', label: 'Sales Quotation', icon: <MdReceipt />, iconColor: '#0f766e' }
      ]
    },
    sales: {
      title: 'Sales',
      icon: <MdTrendingUp />,
      iconColor: '#f59e0b',
      activeIconColor: '#ffffff',
      bgGradient: 'linear-gradient(135deg, #f59e0b, #d97706)',
      items: [
        { to: '/salesOrderIndexTable/1', label: 'Sales Order', icon: <MdShoppingBag />, iconColor: '#f59e0b' },
        { to: '/dcindex/1', label: 'Delivery Challan', icon: <MdLocalShippingIcon />, iconColor: '#f97316' },
        { to: '/salesindex/1', label: 'Sales Invoice', icon: <MdReceipt />, iconColor: '#f59e0b' },
        { to: '/srindex/1', label: 'Sales Rejection', icon: <FaExchangeAlt />, iconColor: '#f97316' }
      ]
    },
    purchase: {
      title: 'Purchase',
      icon: <MdShoppingCart />,
      iconColor: '#8b5cf6',
      activeIconColor: '#ffffff',
      bgGradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
      items: [
        { to: '/poenquiryindex/1', label: 'Purchase Enquiry', icon: <MdAssignment />, iconColor: '#8b5cf6' },
        { to: '/poindex/1', label: 'Purchase Order', icon: <FaClipboardList />, iconColor: '#a78bfa' },
        { to: '/irindex/1', label: 'Inspection', icon: <MdAssignment />, iconColor: '#8b5cf6' },
        { to: '/mrnindex/1', label: 'GRN', icon: <FaTruckLoading />, iconColor: '#a78bfa' },
        { to: '/pindex/1', label: 'Purchase', icon: <MdShoppingCart />, iconColor: '#8b5cf6' },
        { to: '/prindex/1', label: 'Purchase Return', icon: <FaExchangeAlt />, iconColor: '#a78bfa' }
      ]
    },
    production: {
      title: 'Production',
      icon: <MdFactory />,
      iconColor: '#ef4444',
      activeIconColor: '#ffffff',
      bgGradient: 'linear-gradient(135deg, #ef4444, #dc2626)',
      items: [
        { to: '/bomindex/1', label: 'BOM', icon: <MdBuild />, iconColor: '#ef4444' },
        { to: '/Porderindex/1', label: 'Production Order', icon: <FaClipboardList />, iconColor: '#f87171' },
        { to: '/issue-slip-list/1', label: 'Material Issue Slip', icon: <FaBoxOpen />, iconColor: '#ef4444' },
        { to: '/pump-production-process-list/1', label: 'Production Process', icon: <MdBuild />, iconColor: '#f87171' }
      ]
    },
    inventory: {
      title: 'Inventory',
      icon: <MdInventory />,
      iconColor: '#10b981',
      activeIconColor: '#ffffff',
      bgGradient: 'linear-gradient(135deg, #10b981, #059669)',
      items: [
        { to: '/opening-stock-list/1', label: 'Opening Stock', icon: <MdInventory />, iconColor: '#10b981' },
        { to: '/stock-report-quantity', label: 'Stock Report (Qty)', icon: <MdAssessment />, iconColor: '#34d399' },
        { to: '/stock-report-value', label: 'Stock Report (Value)', icon: <MdAssessment />, iconColor: '#10b981' }
      ]
    }
  };

  // Helper function to check if a submenu item is active (including create/edit/view pages)
  const isSubmenuItemActive = (itemTo) => {
    const currentPath = location.pathname;

    // Exact match
    if (currentPath === itemTo) return true;

    // Get base path without trailing numbers (e.g., /productindex/1 -> /productindex)
    const basePath = itemTo.replace(/\/\d+$/, '').replace(/\/1$/, '');

    // Check for create/edit/view pages
    // Examples: /productCreate, /productEdit/123, /productView/123
    const possiblePaths = [
      basePath.replace(/index$/, 'Create'),      // productindex -> productCreate
      basePath.replace(/index$/, 'Edit'),        // productindex -> productEdit
      basePath.replace(/index$/, 'View'),        // productindex -> productView
      basePath + 'Create',                       // productindexCreate
      basePath + 'Edit',                         // productindexEdit
      basePath + 'View',                         // productindexView
    ];

    for (const path of possiblePaths) {
      if (currentPath.includes(path) || currentPath.startsWith(path)) {
        return true;
      }
    }

    // Generic check: if current path starts with the base path (without the /1)
    // and is not the exact same path
    if (currentPath.startsWith(basePath) && currentPath !== itemTo) {
      return true;
    }

    return false;
  };

  // Page title mapping - UPDATE THIS FUNCTION
  const getPageTitle = (pathname) => {
    const exactMatches = {
      '/home': 'Dashboard',
      '/company': 'Company Details',
      '/company/edit': 'Edit Company',
      '/imageupload': 'Upload Drawing',
      '/stock-report-quantity': 'Stock Report (Quantity)',
      '/stock-report-value': 'Stock Report (Value)',
      '/stock-details-quantity': 'Product Stock Details',
      '/stock-details-value': 'Product Stock Value Details',
    };

    if (exactMatches[pathname]) return exactMatches[pathname];

    const patterns = [
      // Staff
      { pattern: '/staffindex/', title: 'Staff List' },
      { pattern: '/staffCreate', title: 'Create Staff' },
      { pattern: '/staffEdit/', title: 'Edit Staff' },
      { pattern: '/staffView/', title: 'View Staff' },

      // Brand
      { pattern: '/brandindex/', title: 'Brand List' },
      { pattern: '/brandCreate', title: 'Create Brand' },
      { pattern: '/brandEdit/', title: 'Edit Brand' },
      { pattern: '/brandView/', title: 'View Brand' },

      // Product
      { pattern: '/productindex/', title: 'Product List' },
      { pattern: '/productCreate', title: 'Create Product' },
      { pattern: '/productEdit/', title: 'Edit Product' },
      { pattern: '/productView/', title: 'View Product' },

      // Warehouse
      { pattern: '/warehouseindex/', title: 'Warehouse List' },
      { pattern: '/warehouseCreate', title: 'Create Warehouse' },
      { pattern: '/warehouseEdit/', title: 'Edit Warehouse' },
      { pattern: '/warehouseView/', title: 'View Warehouse' },

      // Customer
      { pattern: '/customerindex/', title: 'Customer List' },
      { pattern: '/customerCreate', title: 'Create Customer' },
      { pattern: '/customerEdit/', title: 'Edit Customer' },
      { pattern: '/customerView/', title: 'View Customer' },

      // Supplier
      { pattern: '/supplierindex/', title: 'Supplier List' },
      { pattern: '/supplierCreate', title: 'Create Supplier' },
      { pattern: '/supplierEdit/', title: 'Edit Supplier' },
      { pattern: '/supplierView/', title: 'View Supplier' },

      // Branch
      { pattern: '/branchindex/', title: 'Branch List' },
      { pattern: '/branchCreate', title: 'Create Branch' },
      { pattern: '/branchEdit/', title: 'Edit Branch' },
      { pattern: '/branchView/', title: 'View Branch' },

      // Group
      { pattern: '/groupindex/', title: 'Group List' },
      { pattern: '/groupCreate', title: 'Create Group' },
      { pattern: '/groupEdit/', title: 'Edit Group' },
      { pattern: '/groupView/', title: 'View Group' },

      // Department - YAHI ADD KIYA HAI
      { pattern: '/department-list/', title: 'Department List' },
      { pattern: '/department-add', title: 'Create Department' },
      { pattern: '/department-view/', title: 'View Department' },
      { pattern: '/department-edit/', title: 'Edit Department' },

      // Purchase Enquiry
      { pattern: '/poenquiryindex/', title: 'Purchase Enquiry List' },
      { pattern: '/poenquiryindex/create', title: 'Create Purchase Enquiry' },
      { pattern: '/poenquiryEdit/', title: 'Edit Purchase Enquiry' },
      { pattern: '/poenquiryView/', title: 'View Purchase Enquiry' },

      // Purchase Order
      { pattern: '/poindex/', title: 'Purchase Order List' },
      { pattern: '/poCreate', title: 'Create Purchase Order' },
      { pattern: '/poEdit/', title: 'Edit Purchase Order' },
      { pattern: '/poView/', title: 'View Purchase Order' },

      // MRN / GRN
      { pattern: '/mrnindex/', title: 'GRN List' },
      { pattern: '/mrnCreate', title: 'Create GRN' },
      { pattern: '/mrnEdit/', title: 'Edit GRN' },
      { pattern: '/mrnView/', title: 'View GRN' },

      // Inspection
      { pattern: '/irindex/', title: 'Inspection List' },
      { pattern: '/irCreate', title: 'Create Inspection' },
      { pattern: '/irEdit/', title: 'Edit Inspection' },
      { pattern: '/irView/', title: 'View Inspection' },

      // Purchase
      { pattern: '/pindex/', title: 'Purchase List' },
      { pattern: '/pCreate', title: 'Create Purchase' },
      { pattern: '/pEdit/', title: 'Edit Purchase' },
      { pattern: '/pView/', title: 'View Purchase' },

      // Purchase Return
      { pattern: '/prindex/', title: 'Purchase Return List' },
      { pattern: '/prCreate', title: 'Create Purchase Return' },
      { pattern: '/prView/', title: 'View Purchase Return' },

      // Sales Enquiry
      { pattern: '/salesEnquiryIndex/', title: 'Sales Enquiry List' },
      { pattern: '/createSalesEnquiry', title: 'Create Sales Enquiry' },
      { pattern: '/editSalesEnquiry/', title: 'Edit Sales Enquiry' },
      { pattern: '/viewSalesEnquiry/', title: 'View Sales Enquiry' },

      // Quotation
      { pattern: '/quotationIndexTable/', title: 'Quotation List' },
      { pattern: '/createQuotation', title: 'Create Quotation' },
      { pattern: '/editQuotation/', title: 'Edit Quotation' },
      { pattern: '/viewQuotation/', title: 'View Quotation' },

      // Sales Order
      { pattern: '/salesOrderIndexTable/', title: 'Sales Order List' },
      { pattern: '/createSalesOrder', title: 'Create Sales Order' },
      { pattern: '/editSalesOrder/', title: 'Edit Sales Order' },
      { pattern: '/viewSalesOrder/', title: 'View Sales Order' },

      // Delivery Challan
      { pattern: '/dcindex/', title: 'Delivery Challan List' },
      { pattern: '/dcCreate', title: 'Create Delivery Challan' },
      { pattern: '/dcEdit/', title: 'Edit Delivery Challan' },
      { pattern: '/dcView/', title: 'View Delivery Challan' },

      // Sales Invoice
      { pattern: '/salesindex/', title: 'Sales Invoice List' },
      { pattern: '/salesCreate', title: 'Create Sales Invoice' },
      { pattern: '/salesEdit/', title: 'Edit Sales Invoice' },
      { pattern: '/salesView/', title: 'View Sales Invoice' },

      // Sales Return
      { pattern: '/srindex/', title: 'Sales Return List' },
      { pattern: '/srCreate', title: 'Create Sales Return' },
      { pattern: '/srView/', title: 'View Sales Return' },

      // BOM
      { pattern: '/bomindex/', title: 'BOM List' },
      { pattern: '/BOMCreate', title: 'Create BOM' },
      { pattern: '/BOMEdit/', title: 'Edit BOM' },
      { pattern: '/BOMView/', title: 'View BOM' },

      // Production Order
      { pattern: '/Porderindex/', title: 'Production Order List' },
      { pattern: '/PorderCreate', title: 'Create Production Order' },
      { pattern: '/PorderUpdate/', title: 'Edit Production Order' },
      { pattern: '/PorderView/', title: 'View Production Order' },

      // Issue Slip
      { pattern: '/issue-slip-list/', title: 'Issue Slip List' },
      { pattern: '/IssueSlipCreate', title: 'Create Issue Slip' },
      { pattern: '/IssueSlipUpdate/', title: 'Edit Issue Slip' },
      { pattern: '/IssueSlipView/', title: 'View Issue Slip' },

      // Production Process
      { pattern: '/pump-production-process-list/', title: 'Production Process List' },
      { pattern: '/pump-production-process-add', title: 'Create Production Process' },
      { pattern: '/pump-production-process-update/', title: 'Edit Production Process' },
      { pattern: '/pump-production-process-view/', title: 'View Production Process' },

      // Opening Stock
      { pattern: '/opening-stock-list/', title: 'Opening Stock List' },
      { pattern: '/opening-stock-add', title: 'Create Opening Stock' },
      { pattern: '/opening-stock-update/', title: 'Edit Opening Stock' },
      { pattern: '/opening-stock-view/', title: 'View Opening Stock' },

      // Drawing/Image
      { pattern: '/imagetabledindex/', title: 'Drawing List' },
      { pattern: '/imageView/', title: 'View Drawing' },

      // Contact
      { pattern: '/contactTable/', title: 'Contact List' },
      { pattern: '/contactCreate/', title: 'Create Contact' },
      { pattern: '/contactEdit/', title: 'Edit Contact' },
      { pattern: '/contactView/', title: 'View Contact' },
    ];

    for (const p of patterns) {
      if (pathname.includes(p.pattern)) {
        return p.title;
      }
    }

    // Print routes
    if (pathname.includes('/salesInvoice/print/')) return 'Sales Invoice Print';
    if (pathname.includes('/salesInvoiceInternational/print/')) return 'Sales Invoice Print (International)';
    if (pathname.includes('/salesInvoiceManufacturingInternational/print/')) return 'Sales Invoice Print (Manufacturing)';
    if (pathname.includes('/Sales-Invoice-Manufacturing-Domestic/print/')) return 'Sales Invoice Print (Domestic)';
    if (pathname.includes('/Purchses/PurchaseOrder/AraiManufacturing/PoDomPrint/')) return 'Purchase Order Print';

    // Convert routes
    if (pathname.includes('/convert')) return 'Convert Document';
    if (pathname.includes('/sotodcconvert/')) return 'SO to DC Convert';
    if (pathname.includes('/dctosalesconvert/')) return 'DC to Sales Convert';
    if (pathname.includes('/sotosalesconvert/')) return 'SO to Sales Convert';
    if (pathname.includes('/stosrconvert/')) return 'Sales to Return Convert';
    if (pathname.includes('/setosqconversion/')) return 'SE to SQ Convert';
    if (pathname.includes('/sqtosoconversion/')) return 'SQ to SO Convert';
    if (pathname.includes('/ptoprconvert/')) return 'Purchase to Return Convert';
    if (pathname.includes('/petopoconvert/')) return 'PE to PO Convert';

    // Default for dynamic routes with ID
    const pathParts = pathname.split('/').filter(p => p);
    if (pathParts.length > 0) {
      const lastPart = pathParts[pathParts.length - 1];
      if (!isNaN(lastPart) && pathParts.length > 1) {
        const pageName = pathParts[pathParts.length - 2]?.replace(/-/g, ' ') || 'Details';
        return pageName.charAt(0).toUpperCase() + pageName.slice(1);
      }
    }

    return 'Dashboard';
  };

  // Update active link based on current path
  useEffect(() => {
    const currentPath = location.pathname;
    let activeSection = 'dashboard';

    // Check dashboard
    if (navData.dashboard.path === currentPath) {
      activeSection = 'dashboard';
    } else {
      // Check each section for matching items
      for (const [key, section] of Object.entries(navData)) {
        if (section.items) {
          const found = section.items.some(item => isSubmenuItemActive(item.to));
          if (found) {
            activeSection = key;
            break;
          }
        }
      }
    }

    setActiveLink(activeSection);

    // Auto-expand the active section when sidebar is open
    if (isOpen && activeSection !== 'dashboard') {
      setExpandedSections(prev => {
        const newState = {
          masters: false,
          userMasters: false,
          crm: false,
          sales: false,
          purchase: false,
          production: false,
          inventory: false
        };
        if (newState.hasOwnProperty(activeSection)) {
          newState[activeSection] = true;
        }
        return newState;
      });
    }
  }, [location.pathname, isOpen]);

  // Update page title
  useEffect(() => {
    setCurrentPageTitle(getPageTitle(location.pathname));
  }, [location.pathname]);

  const allSuggestions = Object.values(navData).flatMap(section => {
    if (section.items) {
      return section.items.map(item => ({
        ...item,
        section: section.title,
        sectionColor: section.iconColor
      }));
    }
    return section.path ? [{
      to: section.path,
      label: section.title,
      section: section.title,
      icon: section.icon,
      sectionColor: section.iconColor
    }] : [];
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.trim() === '') { setSuggestions([]); return; }
      const filtered = allSuggestions.filter(s =>
        s.label.toLowerCase().includes(searchInput.toLowerCase()) ||
        s.section.toLowerCase().includes(searchInput.toLowerCase())
      );
      setSuggestions(filtered.slice(0, 8));
      setSelectedIndex(-1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    document.body.setAttribute('data-theme', isDayMode ? 'day' : 'night');
  }, [isDayMode]);

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
        .then(() => setIsFullScreen(true))
        .catch((err) => console.error(`Error: ${err.message}`));
    } else {
      document.exitFullscreen()
        .then(() => setIsFullScreen(false))
        .catch((err) => console.error(`Error: ${err.message}`));
    }
  };

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullScreenChange);
  }, []);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        document.querySelector('.search-input')?.focus();
      }
      if (e.key === 'F11') {
        e.preventDefault();
        toggleFullScreen();
      }
    };
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, []);

  const handleSearchChange = (e) => setSearchInput(e.target.value);
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (selectedIndex >= 0 && suggestions[selectedIndex]) navigate(suggestions[selectedIndex].to);
      else if (suggestions.length > 0) navigate(suggestions[0].to);
      setSearchInput(''); setSuggestions([]);
    }
  };
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(p => p < suggestions.length - 1 ? p + 1 : p); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(p => p > 0 ? p - 1 : p); }
  };
  const handleSuggestionClick = (path) => {
    navigate(path); setSearchInput(''); setSuggestions([]);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openCard === 'userCard' && profileRef.current && !profileRef.current.contains(event.target))
        setOpenCard(null);
      if (searchRef.current && !searchRef.current.contains(event.target))
        setSuggestions([]);
      if (mobileMenuOpen && sidebarRef.current && !sidebarRef.current.contains(event.target))
        setMobileMenuOpen(false);
      if (hoveredIcon && !event.target.closest('.icon-flyout-menu') && !event.target.closest('.sidebar-icon-btn')) {
        setHoveredIcon(null); currentOpenIconRef.current = null;
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openCard, mobileMenuOpen, hoveredIcon]);

  const toggleSidebar = () => {
    if (window.innerWidth <= 768) setMobileMenuOpen(!mobileMenuOpen);
    else setIsOpen(!isOpen);
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => {
      if (prev[sectionKey]) {
        return { masters: false, userMasters: false, crm: false, sales: false, purchase: false, production: false, inventory: false };
      }
      return { masters: false, userMasters: false, crm: false, sales: false, purchase: false, production: false, inventory: false, [sectionKey]: true };
    });
  };

  const handleLogout = async () => {
    const result = await Swal.fire({
      title: 'Logout Confirmation',
      text: 'Are you sure you want to logout?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0d9488',
      cancelButtonColor: '#ef4444',
      confirmButtonText: 'Yes, logout',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await logout();
        navigate('/login');
      } catch (error) {
        Swal.fire({ title: 'Error', text: 'Logout failed', icon: 'error', confirmButtonColor: '#0d9488' });
      }
    }
  };

  const primaryRole = user?.roles?.[0]?.role.replace('ROLE_', '') || 'User';
  const getInitials = () => {
    if (user?.firstname && user?.lastname)
      return `${user.firstname.charAt(0)}${user.lastname.charAt(0)}`.toUpperCase();
    return user?.username?.charAt(0).toUpperCase() || 'U';
  };
  const closeMobileMenu = () => setMobileMenuOpen(false);

  const handleIconMouseEnter = (key, e) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    if (currentOpenIconRef.current && currentOpenIconRef.current !== key) setHoveredIcon(null);
    const rect = e.currentTarget.getBoundingClientRect();
    setFlyoutPos({ top: rect.top, left: rect.right + 8 });
    setHoveredIcon(key);
    currentOpenIconRef.current = key;
  };

  const handleIconMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredIcon(null); currentOpenIconRef.current = null;
    }, 300);
  };

  const handleFlyoutMouseEnter = () => { if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current); };
  const handleFlyoutMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredIcon(null); currentOpenIconRef.current = null;
    }, 300);
  };

  const FlyoutPortal = () => {
    if (!hoveredIcon) return null;
    const section = navData[hoveredIcon];
    if (!section) return null;

    return ReactDOM.createPortal(
      <div
        className="icon-flyout-menu"
        style={{ top: flyoutPos.top, left: flyoutPos.left }}
        onMouseEnter={handleFlyoutMouseEnter}
        onMouseLeave={handleFlyoutMouseLeave}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flyout-header" style={{ background: section.bgGradient }}>
          <span className="flyout-icon-small">
            {React.cloneElement(section.icon, { style: { fontSize: '14px', color: 'white' } })}
          </span>
          <span className="flyout-title">{section.title}</span>
        </div>

        {section.items ? (
          <ul className="flyout-list">
            {section.items.map((item, index) => {
              const isActive = isSubmenuItemActive(item.to);
              return (
                <li key={index}>
                  <div
                    className={`flyout-item ${isActive ? 'active' : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault(); e.stopPropagation();
                      navigate(item.to);
                      setActiveLink(hoveredIcon);
                      setTimeout(() => { setHoveredIcon(null); currentOpenIconRef.current = null; }, 100);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="flyout-item-icon" style={{ color: isActive ? item.iconColor : '#64748b' }}>
                      {React.cloneElement(item.icon, { style: { fontSize: '14px' } })}
                    </span>
                    <span className="flyout-item-label">{item.label}</span>
                    {isActive && <span className="flyout-active-dot"></span>}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <ul className="flyout-list">
            <li>
              <div
                className={`flyout-item ${activeLink === hoveredIcon ? 'active' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  navigate(section.path);
                  setActiveLink(hoveredIcon);
                  setTimeout(() => { setHoveredIcon(null); currentOpenIconRef.current = null; }, 100);
                }}
                style={{ cursor: 'pointer' }}
              >
                <span className="flyout-item-icon" style={{ color: activeLink === hoveredIcon ? section.iconColor : '#64748b' }}>
                  {React.cloneElement(section.icon, { style: { fontSize: '14px' } })}
                </span>
                <span className="flyout-item-label">Go to {section.title}</span>
                {activeLink === hoveredIcon && <span className="flyout-active-dot"></span>}
              </div>
            </li>
          </ul>
        )}
      </div>,
      document.body
    );
  };

  return (
    <div className="erp-layout">

      {/* TOP NAVBAR */}
      <nav className="top-navbar">
        <div className="navbar-left">
          <button className="mobile-menu-toggle" onClick={toggleSidebar}>
            {mobileMenuOpen ? <MdClose size={20} /> : <MdMenu size={20} />}
          </button>

          <div className="logo-container">
            <div className="logo-wrapper">
              <img src={companyLogo} alt="SYNERGY" className="company-logo-image" />
            </div>
          </div>

          <button className="sidebar-toggle-btn" onClick={toggleSidebar}>
            {isOpen ? <MdKeyboardDoubleArrowLeft size={20} /> : <MdKeyboardDoubleArrowRight size={20} />}
          </button>

          <div className="page-title-container">
            <span className="page-title-separator">/</span>
            <span className="page-title">{currentPageTitle}</span>
          </div>
        </div>

        <div className="navbar-center" ref={searchRef}>
          <div className="search-container">
            <div className="search-input-wrapper">
              <FiSearch className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Search modules, features... (Ctrl+K)"
                value={searchInput}
                onChange={handleSearchChange}
                onKeyPress={handleSearchKeyPress}
                onKeyDown={handleKeyDown}
              />
              <span className="search-shortcut">⌘K</span>
            </div>

            {suggestions.length > 0 && (
              <div className="search-suggestions">
                <div className="suggestions-header">
                  <span>Quick Actions</span>
                  <span>{suggestions.length} results</span>
                </div>
                <ul className="suggestions-list">
                  {suggestions.map((suggestion, index) => (
                    <li
                      key={index}
                      className={`suggestion-item ${selectedIndex === index ? 'selected' : ''}`}
                      onClick={() => handleSuggestionClick(suggestion.to)}
                    >
                      <div className="suggestion-icon" style={{ color: suggestion.sectionColor }}>
                        {suggestion.icon}
                      </div>
                      <div className="suggestion-content">
                        <div className="suggestion-label">{suggestion.label}</div>
                        <div className="suggestion-section">{suggestion.section}</div>
                      </div>
                      <MdChevronRight className="suggestion-arrow" />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="navbar-right">
          <button
            className="fullscreen-toggle-btn"
            onClick={toggleFullScreen}
            title={isFullScreen ? 'Exit Full Screen (F11)' : 'Enter Full Screen (F11)'}
          >
            {isFullScreen ? <MdFullscreenExit size={18} /> : <MdFullscreen size={18} />}
          </button>

          <button
            className="theme-toggle-btn"
            onClick={() => setIsDayMode(prev => !prev)}
            title={isDayMode ? 'Switch to Night Mode' : 'Switch to Day Mode'}
          >
            {isDayMode ? <SunIcon /> : <MoonIcon />}
          </button>

          <button className="notification-btn">
            <FaBell className="notification-icon" />
            <span className="notification-badge">3</span>
          </button>

          <div className="user-profile-container" ref={profileRef}>
            <div className="user-avatar" onClick={() => setOpenCard(openCard === 'userCard' ? null : 'userCard')}>
              {user ? (
                <div className="avatar">
                  <span className="avatar-text">{getInitials()}</span>
                  <div className="online-indicator"></div>
                </div>
              ) : (
                <MdAccountCircle className="default-avatar" />
              )}
            </div>

            {openCard === 'userCard' && user && (
              <div className="user-card-compact">
                <div className="user-card-header-compact">
                  <div className="avatar-compact">
                    <span className="avatar-text-compact">{getInitials()}</span>
                  </div>
                  <div className="user-info-compact">
                    <h4 className="user-name-compact">{user.firstname} {user.lastname}</h4>
                    <span className="role-badge-compact">
                      <FaUserShield size={10} /> {primaryRole}
                    </span>
                  </div>
                  <div className="status-indicator-compact">
                    <div className="status-dot-compact"></div>
                  </div>
                </div>

                <div className="user-details-compact">
                  <div className="detail-item-compact">
                    <MdEmail className="detail-icon-compact" />
                    <div className="detail-text-compact">
                      <span className="detail-label-compact">Email</span>
                      <span className="detail-value-compact">{user.email}</span>
                    </div>
                  </div>
                  <div className="detail-item-compact">
                    <MdPhone className="detail-icon-compact" />
                    <div className="detail-text-compact">
                      <span className="detail-label-compact">Phone</span>
                      <span className="detail-value-compact">{user.mobileno || 'Not provided'}</span>
                    </div>
                  </div>
                  <div className="detail-item-compact">
                    <MdPerson className="detail-icon-compact" />
                    <div className="detail-text-compact">
                      <span className="detail-label-compact">Department</span>
                      <span className="detail-value-compact">{user.departmentname || user.department?.name || 'Not assigned'}</span>
                    </div>
                  </div>
                </div>

                <div className="user-actions-compact">
                  <Link to={`/staffEdit/${user.id}`} className="action-btn-compact edit-btn-compact" onClick={() => setOpenCard(null)}>
                    <MdEdit size={14} /><span>Edit</span>
                  </Link>
                  <button className="action-btn-compact logout-btn-compact" onClick={handleLogout}>
                    <MdLogout size={14} /><span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* SIDEBAR */}
      <aside
        ref={sidebarRef}
        className={`sidebar-container ${isOpen ? 'expanded' : 'collapsed'} ${mobileMenuOpen ? 'mobile-open' : ''}`}
      >
        {isOpen ? (
          <div className="sidebar-menu">
            <div className="sidebar-header-section">
              <h3 className="sidebar-title">MAIN NAVIGATION</h3>
            </div>

            {Object.entries(navData).map(([key, section]) => (
              <div key={key} className="sidebar-section">
                {section.path ? (
                  <Link
                    to={section.path}
                    className={`sidebar-item ${activeLink === key ? 'active' : ''}`}
                    onClick={() => setActiveLink(key)}
                  >
                    <div
                      className="sidebar-icon-wrapper"
                      style={{
                        background: activeLink === key ? section.bgGradient : 'rgba(255, 255, 255, 0.1)',
                        color: activeLink === key ? section.activeIconColor : section.iconColor
                      }}
                    >
                      {React.cloneElement(section.icon, {
                        style: { color: activeLink === key ? section.activeIconColor : section.iconColor, fontSize: '18px' }
                      })}
                    </div>
                    <span className="sidebar-text">{section.title}</span>
                    {activeLink === key && <div className="active-indicator"></div>}
                  </Link>
                ) : (
                  <>
                    <div
                      className="sidebar-header"
                      onClick={() => toggleSection(key)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div
                        className="sidebar-icon-wrapper"
                        style={{
                          background: expandedSections[key] ? section.bgGradient : 'rgba(255, 255, 255, 0.1)',
                          color: expandedSections[key] ? section.activeIconColor : section.iconColor
                        }}
                      >
                        {React.cloneElement(section.icon, {
                          style: { color: expandedSections[key] ? section.activeIconColor : section.iconColor, fontSize: '18px' }
                        })}
                      </div>
                      <span className="sidebar-text">{section.title}</span>
                      <MdExpandMore className={`expand-icon ${expandedSections[key] ? 'expanded' : ''}`} />
                    </div>

                    {expandedSections[key] && (
                      <div className="submenu">
                        {section.items?.map((item, index) => {
                          // 🔧 FIX: Use the new active check function
                          const isActive = isSubmenuItemActive(item.to);
                          return (
                            <Link
                              key={index}
                              to={item.to}
                              className={`submenu-item ${isActive ? 'active' : ''}`}
                              onClick={() => setActiveLink(key)}
                            >
                              <div className="submenu-icon-wrapper" style={{ color: isActive ? item.iconColor : 'rgba(255, 255, 255, 0.7)' }}>
                                {React.cloneElement(item.icon, {
                                  style: { color: isActive ? item.iconColor : 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }
                                })}
                              </div>
                              <span className="submenu-text">{item.label}</span>
                              {isActive && <div className="submenu-indicator"></div>}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}

            <div className="sidebar-footer">
              <div className="quick-actions-title">QUICK ACTIONS</div>
              <div className="quick-actions-grid">
                <button className="quick-action" title="Settings"><FaCogs size={16} /></button>
                <button className="quick-action" title="Reports"><FaChartLine size={16} /></button>
                <button className="quick-action" title="Help"><MdHelp size={16} /></button>
              </div>
            </div>
          </div>
        ) : (
          <div className="sidebar-icons">
            <div className="icons-header">
              <div className="logo-circle-small">
                <img src={companyLogo} alt="SYNERGY" className="company-logo-image-small" />
              </div>
            </div>

            <div className="icons-container">
              {Object.entries(navData).map(([key, section]) => (
                <div
                  key={key}
                  className="icon-flyout-wrapper"
                  onMouseEnter={(e) => handleIconMouseEnter(key, e)}
                  onMouseLeave={handleIconMouseLeave}
                >
                  <div
                    className={`sidebar-icon-btn ${activeLink === key ? 'active' : ''}`}
                    title={section.title}
                  >
                    {React.cloneElement(section.icon, {
                      style: {
                        color: activeLink === key ? section.iconColor : 'rgba(255, 255, 255, 0.7)',
                        fontSize: '20px'
                      }
                    })}
                    {activeLink === key && <div className="active-dot"></div>}
                  </div>
                </div>
              ))}
            </div>

            <div className="icons-footer">
              <button className="icon-action-btn" title="Settings"><FaCogs size={16} /></button>
            </div>
          </div>
        )}
      </aside>

      <FlyoutPortal />

      <main className={`main-content ${isOpen ? 'expanded' : 'collapsed'}`}>
        <div className="content-wrapper">
          {children}
        </div>
      </main>

      {mobileMenuOpen && (
        <div className="mobile-overlay" onClick={closeMobileMenu}></div>
      )}
    </div>
  );
};

export default Sidebar;